import { useEffect, useRef, useState } from "react";

/**
 * HeroBackground
 * - Plays /hero-loop.mp4 as a muted, autoplaying, looping background video.
 * - Falls back to a still image on:
 *    a) viewport widths below 768px
 *    b) connections with effectiveType '2g'/'slow-2g'/'3g' or saveData enabled
 *    c) user preference: prefers-reduced-motion
 * - A cream overlay preserves contrast for the hero copy.
 */
export default function HeroBackground() {
  const [allowVideo, setAllowVideo] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const decide = () => {
      if (typeof window === "undefined") return false;
      // a) viewport
      const isMobile = window.matchMedia("(max-width: 767px)").matches;
      if (isMobile) return false;
      // b) connection awareness
      const conn =
        navigator.connection ||
        navigator.mozConnection ||
        navigator.webkitConnection;
      if (conn) {
        if (conn.saveData) return false;
        const slow = ["slow-2g", "2g", "3g"];
        if (slow.includes(conn.effectiveType)) return false;
      }
      // c) reduced motion
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches)
        return false;
      return true;
    };

    setAllowVideo(decide());
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = () => setAllowVideo(decide());
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);

  return (
    <div
      className="absolute inset-0 overflow-hidden"
      data-testid="hero-background"
      aria-hidden="true"
    >
      {/* Image fallback: always present, hidden when video plays */}
      <img
        src="/hero-poster.jpg"
        alt=""
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
          allowVideo ? "opacity-0" : "opacity-100"
        }`}
        data-testid="hero-fallback-image"
      />

      {/* Video (desktop + good network only) */}
      {allowVideo && (
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          src="/hero-loop.mp4"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="/hero-poster.jpg"
          data-testid="hero-video"
        />
      )}

      {/* Cream overlay to preserve hero text contrast */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(253,251,247,0.78) 0%, rgba(253,251,247,0.62) 45%, rgba(253,251,247,0.86) 100%)",
        }}
      />
      {/* Subtle grain texture on top */}
      <div className="absolute inset-0 grain opacity-60" />
    </div>
  );
}
